import { useEffect } from 'react';

import { useImageZoom } from '@/hooks/useImageZoom';

// The receipt itself, zoomable. Shares its gesture handling with the Files tab's
// photo lightbox — a receipt and a site photo are both "an image someone needs to
// look at closely", and the fingers doing the looking are the same fingers.
//
// No `onSwipe`: a receipt is shown on its own, so a horizontal drag has nowhere
// to go and does nothing.

interface ReceiptImageViewerProps {
  src: string;
  alt?: string;
}

export function ReceiptImageViewer({ src, alt = 'Receipt' }: ReceiptImageViewerProps) {
  const { isZoomed, reset, imageStyle, surfaceRef, surfaceProps } = useImageZoom();

  // Switching receipts mid-batch shouldn't land the next one already magnified.
  useEffect(() => {
    reset();
  }, [src, reset]);

  return (
    <div
      ref={surfaceRef}
      data-testid="receipt-zoom-surface"
      className="relative flex h-full w-full items-center justify-center overflow-hidden bg-gray-900"
      // Without this iOS Safari takes the pinch as a page zoom and the receipt
      // never sees it.
      style={{ touchAction: 'none', cursor: isZoomed ? 'grab' : 'default' }}
      {...surfaceProps}
    >
      <img
        src={src}
        alt={alt}
        className="pointer-events-none max-h-full max-w-full select-none object-contain"
        style={imageStyle}
        draggable={false}
      />

      {/* Mouse-only instructions, so they stay off the phone where they are both
          wrong and expensive — the band has no vertical space to give up. */}
      <div className="absolute bottom-3 right-3 hidden rounded bg-black/40 px-3 py-1.5 text-xs text-white/60 sm:block">
        Scroll to zoom · Drag to pan · Double-click to reset
      </div>
    </div>
  );
}
